// src/pages/appointments/book/AppointmentBook.jsx
import React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import TopBarCenter from "@/sot/primitives/TopBarCenter.jsx";
import List from "@/sot/primitives/List.jsx";
import AfriPill from "@/sot/primitives/AfriPill.jsx";
import Button from "@/components/ui/Button.jsx";
import Card from "@/components/ui/Card.jsx";
import Radio from "@/components/ui/Radio.jsx";
import ContextFeedbackLauncher from "@/components/feedback/ContextFeedbackLauncher.jsx";
import { supabase } from "@/services/supabaseClient";
import HospitalFillIcon from "@/icons/afrimed/hospital_Fill.svg";

import { useAppointmentsFilters } from "@/pages/appointments/AppointmentsFiltersContext.jsx";
import {
  getDoctorBookableDates,
  getDoctorBookingAvailability,
} from "@/services/doctorAvailabilityService.js";
import { resolveDoctorBookingEntryState } from "@/services/doctorBookingMode.js";
import {
  DOCTOR_ACTIVE_PAYMENT_METHOD_OPTIONS,
  loadDoctorPaymentMethods,
} from "@/services/doctorPaymentPreferences.js";
import {
  filterAppointmentPaystackMethodsForPatient,
  recordAppointmentPaymentCapabilityDiagnostics,
} from "@/services/appointmentPaystackCapabilities.js";
import { loadSupportedAppointmentPaymentMethods } from "@/services/appointmentPaymentCapabilities.js";
import { useAuth } from "@/contexts/AuthContext.jsx";
import { resolveInsuranceDisplay } from "@/pages/appointments/insuranceVisibility.js";
import {
  buildBookingCareSubjectOptions,
  buildSelfCareSubject,
  listGuardianDependants,
  normalizeCareSubjectFromBooking,
  resolveDependantAvatarSrc,
} from "@/services/dependantCareService.js";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import { writeUserActivityEvent } from "@/services/userTruthTracking.js";
import { localizeBookingServiceName } from "@/services/bookingServiceLocalization.js";

function todayISO() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

async function filterDatesWithVisibleSlots({
  doctorId,
  dates,
  requestedDuration,
}) {
  const candidateDates = Array.isArray(dates) ? dates : [];
  if (!doctorId || candidateDates.length === 0) return [];

  const checks = await Promise.all(
    candidateDates.map(async (dateISO) => {
      const result = await getDoctorBookingAvailability({
        doctorId,
        dateISO,
        requestedDuration: requestedDuration || null,
      });
      const hasFutureAvailableSlots = (result?.slots || []).some(
        (slot) => slot?.status === "available" && slot?.available === true
      );
      return hasFutureAvailableSlots ? dateISO : "";
    })
  );

  return checks.filter(Boolean);
}

function useAvailabilityClock() {
  const [clock, setClock] = React.useState(() => Date.now());

  React.useEffect(() => {
    if (typeof window === "undefined") return undefined;

    let timeoutId = 0;
    const refreshClock = () => setClock(Date.now());
    const scheduleNextTick = () => {
      const now = Date.now();
      const msUntilNextMinute = 60000 - (now % 60000);
      timeoutId = window.setTimeout(() => {
        refreshClock();
        scheduleNextTick();
      }, msUntilNextMinute || 60000);
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        refreshClock();
      }
    };

    scheduleNextTick();
    window.addEventListener("focus", refreshClock);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.clearTimeout(timeoutId);
      window.removeEventListener("focus", refreshClock);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  return clock;
}

function formatBookableDateParts(dateISO, formatDate) {
  const parsed = new Date(`${dateISO}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) {
    return {
      weekday: "",
      day: "",
      month: "",
    };
  }

  return {
    weekday: formatDate(parsed, { weekday: "short" }),
    day: formatDate(parsed, { day: "numeric" }),
    month: formatDate(parsed, { month: "short" }),
  };
}

function getBookableMonthKey(dateISO) {
  const parsed = new Date(`${dateISO}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return "";
  return `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, "0")}`;
}

function formatBookableMonthLabel(dateISO, formatDate) {
  const parsed = new Date(`${dateISO}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return "";
  return formatDate(parsed, { month: "long" });
}

function safeJSONParse(raw) {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function createBookingKey() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `booking-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function formatAmount(value, currency = "BWP") {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "";
  return `${amount.toFixed(2)} ${String(currency || "BWP").trim() || "BWP"}`;
}

function toFiniteAmount(value) {
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : null;
}

function normalizeDurationMinutes(value, fallback = 30) {
  const minutes = Number(value);
  if (Number.isFinite(minutes) && minutes > 0) return Math.round(minutes);
  return fallback;
}

function localizePaymentMethodLabel(
  value,
  t = (_, fallback) => fallback,
  fallback = "",
) {
  const raw = String(value || "").trim().toLowerCase();
  if (!raw) {
    return fallback || t("appointments.book.sections.payment", "Select your payment method");
  }

  if (
    raw === "card" ||
    raw === "credit" ||
    raw === "debit" ||
    raw.includes("card") ||
    raw.includes("visa") ||
    raw.includes("mastercard") ||
    raw.includes("amex")
  ) {
    return t("appointments.checkout.cardMethod", "Credit • Debit Card");
  }
  if (raw === "instant_eft" || raw === "eft" || raw.includes("instant eft")) return t("appointments.checkout.instantEft", "Instant EFT");
  if (raw === "capitec_pay") return t("appointments.checkout.capitecPay", "Capitec Pay");
  if (raw === "qr") return t("appointments.checkout.scanToPay", "Scan to Pay");
  if (raw === "apple_pay") return t("appointments.checkout.applePay", "Apple Pay");
  if (raw === "google_pay") return t("appointments.checkout.googlePay", "Google Pay");
  if (raw === "samsung_pay") return t("appointments.checkout.samsungPay", "Samsung Pay");
  if (raw === "bank" || raw.includes("bank") || raw.includes("transfer")) return fallback || value;
  if (raw === "cash" || raw.includes("cash")) {
    return t("appointments.confirm.payment.cash", "Cash on service");
  }
  return fallback || value;
}

function normalizeInsurancePaymentMode(value) {
  const raw = String(value || "").trim().toLowerCase();
  if (raw === "insurance" || raw === "use_insurance") return "insurance";
  return "self_pay";
}

function buildInsuranceContext({
  mode = "self_pay",
  provider = "",
  acceptedLabel = "",
  companies = [],
  t = (_, fallback) => fallback,
} = {}) {
  const normalizedMode = normalizeInsurancePaymentMode(mode);
  const cleanProvider = String(provider || "").trim();
  const cleanCompanies = (Array.isArray(companies) ? companies : [])
    .map((company) => String(company || "").trim())
    .filter(Boolean);
  const cleanAcceptedLabel = String(acceptedLabel || "").trim();

  if (normalizedMode === "insurance") {
    return {
      insurancePaymentMode: "insurance",
      insuranceProvider: cleanProvider,
      insuranceProviderOptions: cleanCompanies,
      insuranceAcceptedLabel: cleanAcceptedLabel,
      insuranceContextLabel: cleanProvider
        ? t("appointments.book.insuranceContext.provider", "Insurance: {provider}", {
            provider: cleanProvider,
          })
        : t("appointments.book.insuranceContext.generic", "Insurance selected"),
    };
  }

  return {
    insurancePaymentMode: "self_pay",
    insuranceProvider: "",
    insuranceProviderOptions: cleanCompanies,
    insuranceAcceptedLabel: cleanAcceptedLabel,
    insuranceContextLabel: t("appointments.book.insuranceContext.selfPay", "Self-pay"),
  };
}

function normalizeMode(value) {
  const raw = String(value || "").trim().toLowerCase();
  if (raw === "evisit" || raw === "virtual" || raw === "telehealth" || raw === "private") {
    return "evisit";
  }
  if (raw === "inperson" || raw === "in-person" || raw === "moh" || raw === "onsite") {
    return "inperson";
  }
  return "";
}

function parseFeesObject(value) {
  if (!value) return null;
  if (typeof value === "object") return value;
  const raw = String(value || "").trim();
  if (!raw || !raw.startsWith("{")) return null;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

function pickFeeFromFeesObject(fees, mode = "default") {
  if (!fees || typeof fees !== "object") return null;
  const candidates =
    mode === "evisit"
      ? [fees.evisit_price, fees.e_visit_price, fees.price, fees.default_price, fees.default_fee]
      : [fees.consultation_fee, fees.consultation_price, fees.default_fee, fees.default_price, fees.price];
  for (const candidate of candidates) {
    const amount = Number(candidate);
    if (Number.isFinite(amount)) return amount;
  }
  return null;
}

function pickCurrencyFromFeesObject(fees, mode = "default") {
  if (!fees || typeof fees !== "object") return "";
  const candidates =
    mode === "evisit"
      ? [fees.evisit_currency, fees.e_visit_currency, fees.currency, fees.default_currency]
      : [fees.consultation_currency, fees.currency, fees.default_currency];
  for (const candidate of candidates) {
    const text = String(candidate || "").trim();
    if (text) return text;
  }
  return "";
}

function normalizeFallbackServices(doctor, bookingMode = null, t = (_, fallback) => fallback) {
  if (!doctor) return [];
  const fees = parseFeesObject(doctor?.consultation_fees);

  if (String(bookingMode || "").toLowerCase() === "evisit") {
    const evisitAmount =
      pickFeeFromFeesObject(fees, "evisit") ??
      toFiniteAmount(doctor?.evisit_price ?? doctor?.e_visit_price);
    if (evisitAmount != null) {
      return [
        {
          id: "fallback-evisit",
          name: t("appointments.book.fallbackServices.evisit", "Virtual consultation"),
          price: evisitAmount,
          currency:
            pickCurrencyFromFeesObject(fees, "evisit") ||
            String(doctor?.currency || "BWP").trim() ||
            "BWP",
          durationMinutes: normalizeDurationMinutes(
            doctor?.serviceDurationMinutes ?? doctor?.duration_minutes ?? doctor?.duration
          ),
        },
      ];
    }
  }

  const primaryAmount = toFiniteAmount(doctor?.serviceLabelPrice);
  if (doctor?.serviceLabel && primaryAmount != null) {
    return [
      {
        id: "fallback-primary",
        name: String(doctor.serviceLabel).trim(),
        price: primaryAmount,
        currency: String(doctor?.currency || "BWP").trim() || "BWP",
        durationMinutes: normalizeDurationMinutes(
          doctor?.serviceDurationMinutes ?? doctor?.duration_minutes ?? doctor?.duration
        ),
      },
    ];
  }

  const fallbackAmount = toFiniteAmount(doctor?.consultationFee);
  if (fallbackAmount != null) {
    return [
      {
        id: "fallback-consultation",
        name: t("appointments.book.fallbackServices.consultation", "Consultation"),
        price: fallbackAmount,
        currency: String(doctor?.currency || "BWP").trim() || "BWP",
        durationMinutes: normalizeDurationMinutes(
          doctor?.serviceDurationMinutes ?? doctor?.duration_minutes ?? doctor?.duration
        ),
      },
    ];
  }

  return [];
}

function SectionLabelWithIcon({ iconSrc = "", iconAlt = "", label = "" }) {
  return (
    <div className="flex items-center gap-2 text-body-medium text-on-surface-var">
      {iconSrc ? (
        <img
          src={iconSrc}
          alt={iconAlt}
          className="h-6 w-6 flex-none"
          draggable="false"
        />
      ) : null}
      <span>{label}</span>
    </div>
  );
}

async function loadDoctorServices(
  doctorUserId,
  fallbackDoctor,
  bookingMode = null,
  t = (_, fallback) => fallback
) {
  if (!doctorUserId) {
    return { services: normalizeFallbackServices(fallbackDoctor, bookingMode, t) };
  }

  const { data, error } = await supabase
    .from("doctor_services")
    .select("id,name,price,currency,duration_minutes,is_active,sort_order,updated_at,created_at")
    .eq("doctor_id", doctorUserId)
    .eq("is_active", true)
    .order("sort_order", { ascending: true })
    .order("updated_at", { ascending: false })
    .order("created_at", { ascending: false });

  if (error) throw error;

  const services = (Array.isArray(data) ? data : [])
    .map((row) => ({
      id: String(row?.id || "").trim(),
      name: String(row?.name || "").trim(),
      price: Number(row?.price || 0),
      currency: String(row?.currency || "BWP").trim() || "BWP",
      durationMinutes: normalizeDurationMinutes(row?.duration_minutes),
    }))
    .filter((row) => row.id && row.name && Number.isFinite(row.price));

  return {
    services:
      services.length > 0
        ? services
        : normalizeFallbackServices(fallbackDoctor, bookingMode, t),
  };
}

function readDoctorRefreshSafe(locationState, id) {
  const fromState = locationState?.doctor;
  if (fromState && String(fromState.id) === String(id)) return fromState;

  try {
    const raw = sessionStorage.getItem(`afrimed:doctor:${id}`);
    const parsed = raw ? safeJSONParse(raw) : null;
    if (parsed && String(parsed.id) === String(id)) return parsed;
  } catch {}

  try {
    const raw = sessionStorage.getItem("afrimed:lastDoctor");
    const parsed = raw ? safeJSONParse(raw) : null;
    if (parsed && String(parsed.id) === String(id)) return parsed;
  } catch {}

  try {
    const raw = localStorage.getItem(`afrimed:doctor:${id}`);
    const parsed = raw ? safeJSONParse(raw) : null;
    if (parsed && String(parsed.id) === String(id)) return parsed;
  } catch {}

  try {
    const raw = localStorage.getItem("afrimed:lastDoctor");
    const parsed = raw ? safeJSONParse(raw) : null;
    if (parsed && String(parsed.id) === String(id)) return parsed;
  } catch {}

  return null;
}

function readBookingMetaRefreshSafe(locationState, id) {
  const fromState = {
    mode: locationState?.mode || null,
    facilityId: locationState?.facilityId || locationState?.facility?.id || null,
    facilityName: locationState?.facilityName || locationState?.facility?.name || "",
    facility: locationState?.facility || null,
  };

  if (fromState.mode || fromState.facilityId || fromState.facilityName) {
    return fromState;
  }

  try {
    const raw = sessionStorage.getItem(`afrimed:booking:${id}`);
    const parsed = raw ? safeJSONParse(raw) : null;
    if (parsed) {
      return {
        mode: parsed?.mode || null,
        facilityId: parsed?.facilityId || parsed?.facility?.id || null,
        facilityName: parsed?.facilityName || parsed?.facility?.name || "",
        facility: parsed?.facility || null,
      };
    }
  } catch {}

  try {
    const raw = localStorage.getItem(`afrimed:booking:${id}`);
    const parsed = raw ? safeJSONParse(raw) : null;
    if (parsed) {
      return {
        mode: parsed?.mode || null,
        facilityId: parsed?.facilityId || parsed?.facility?.id || null,
        facilityName: parsed?.facilityName || parsed?.facility?.name || "",
        facility: parsed?.facility || null,
      };
    }
  } catch {}

  return {
    mode: null,
    facilityId: null,
    facilityName: "",
    facility: null,
  };
}

function pickBookingInsurancePolicy(rows = [], facilityId = "", facilityName = "") {
  const list = Array.isArray(rows) ? rows : [];
  if (list.length === 0) return "";

  const wantedId = String(facilityId || "").trim();
  if (wantedId) {
    const match = list.find((row) => String(row?.id || "").trim() === wantedId);
    if (match?.insurance_policy) return match.insurance_policy;
  }

  const wantedName = String(facilityName || "").trim().toLowerCase();
  if (wantedName) {
    const match = list.find(
      (row) => String(row?.facility_name || "").trim().toLowerCase() === wantedName
    );
    if (match?.insurance_policy) return match.insurance_policy;
  }

  return list.find((row) => String(row?.insurance_policy || "").trim())?.insurance_policy || "";
}

function hasExplicitCareSubjectState(state) {
  if (!state || typeof state !== "object") return false;
  return Boolean(
    state?.careSubject ||
      state?.careSubjectType ||
      state?.careSubjectId ||
      state?.careSubjectName ||
      state?.careSubjectRelationship ||
      state?.care_subject_type ||
      state?.care_subject_dependant_id ||
      state?.care_subject_relationship,
  );
}

function readStoredBookingDraftById(id) {
  try {
    const raw = sessionStorage.getItem(`afrimed:booking:${id}`);
    const parsed = raw ? safeJSONParse(raw) : null;
    if (parsed) return parsed;
  } catch {}

  try {
    const raw = localStorage.getItem(`afrimed:booking:${id}`);
    const parsed = raw ? safeJSONParse(raw) : null;
    if (parsed) return parsed;
  } catch {}

  return null;
}

function mergeCareSubjectFromStored(locationState, storedState) {
  if (!locationState || typeof locationState !== "object") return storedState || null;
  if (!storedState || typeof storedState !== "object") return locationState;
  if (hasExplicitCareSubjectState(locationState) && !hasExplicitCareSubjectState(storedState)) {
    return locationState;
  }

  return {
    ...storedState,
    ...locationState,
    careSubject: storedState?.careSubject ?? locationState?.careSubject ?? null,
    careSubjectType: storedState?.careSubjectType ?? locationState?.careSubjectType ?? null,
    careSubjectId: storedState?.careSubjectId ?? locationState?.careSubjectId ?? null,
    careSubjectName: storedState?.careSubjectName ?? locationState?.careSubjectName ?? "",
    careSubjectRelationship:
      storedState?.careSubjectRelationship ?? locationState?.careSubjectRelationship ?? "",
    careSubjectDateOfBirth:
      storedState?.careSubjectDateOfBirth ?? locationState?.careSubjectDateOfBirth ?? null,
    careSubjectVerificationStatus:
      storedState?.careSubjectVerificationStatus ??
      locationState?.careSubjectVerificationStatus ??
      "",
    careSubjectAvatarPath:
      storedState?.careSubjectAvatarPath ?? locationState?.careSubjectAvatarPath ?? "",
    careSubjectAvatarUrl:
      storedState?.careSubjectAvatarUrl ?? locationState?.careSubjectAvatarUrl ?? "",
    careSubjectAvatarSrc:
      storedState?.careSubjectAvatarSrc ?? locationState?.careSubjectAvatarSrc ?? "",
  };
}

function readBookingDraftRefreshSafe(locationState, id) {
  const storedState = readStoredBookingDraftById(id);

  if (locationState && typeof locationState === "object") {
    const hasState =
      locationState?.doctor ||
      locationState?.bookingKey ||
      locationState?.selectedDate ||
      locationState?.selectedTime ||
      locationState?.careSubject ||
      locationState?.careSubjectType ||
      locationState?.careSubjectId;
    if (hasState) return mergeCareSubjectFromStored(locationState, storedState);
  }

  return storedState;
}

function persistBookingDraftRefreshSafe(id, bookingKey, snapshot) {
  try {
    const payload = JSON.stringify(snapshot);
    sessionStorage.setItem(`afrimed:booking:${id}`, payload);
    sessionStorage.setItem("afrimed:booking:last", payload);
    sessionStorage.setItem(`afrimed:bookingKey:${bookingKey}`, payload);
    localStorage.setItem(`afrimed:booking:${id}`, payload);
    localStorage.setItem("afrimed:booking:last", payload);
    localStorage.setItem(`afrimed:bookingKey:${bookingKey}`, payload);
  } catch {}
}

function readPersistedCareSubjectSelectionRefreshSafe(id, bookingKey) {
  const cleanDoctorId = String(id || "").trim();
  const cleanBookingKey = String(bookingKey || "").trim();
  const keys = [
    cleanBookingKey ? `afrimed:booking:careSubject:${cleanBookingKey}` : "",
    cleanDoctorId ? `afrimed:booking:careSubject:${cleanDoctorId}` : "",
  ].filter(Boolean);

  for (const key of keys) {
    try {
      const fromSession = String(sessionStorage.getItem(key) || "").trim();
      if (fromSession) return fromSession;
    } catch {}
    try {
      const fromLocal = String(localStorage.getItem(key) || "").trim();
      if (fromLocal) return fromLocal;
    } catch {}
  }

  return "";
}

function persistCareSubjectSelectionRefreshSafe(id, bookingKey, selectionId) {
  const cleanSelectionId = String(selectionId || "").trim();
  if (!cleanSelectionId) return;

  const cleanDoctorId = String(id || "").trim();
  const cleanBookingKey = String(bookingKey || "").trim();
  const keys = [
    cleanBookingKey ? `afrimed:booking:careSubject:${cleanBookingKey}` : "",
    cleanDoctorId ? `afrimed:booking:careSubject:${cleanDoctorId}` : "",
  ].filter(Boolean);

  for (const key of keys) {
    try {
      sessionStorage.setItem(key, cleanSelectionId);
    } catch {}
    try {
      localStorage.setItem(key, cleanSelectionId);
    } catch {}
  }
}

export default function AppointmentBook() {
  const nav = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const auth = useAuth() || {};
  const { t, languageCode, formatDate } = useLanguage();

  const { selectedDate, setSelectedDate, selectedTime, setSelectedTime } = useAppointmentsFilters();

  const doctor = React.useMemo(() => readDoctorRefreshSafe(location.state, id), [location.state, id]);
  const doctorId = React.useMemo(
    () => String(doctor?.id || id || "").trim(),
    [doctor?.id, id]
  );
  const bookingMeta = React.useMemo(
    () => readBookingMetaRefreshSafe(location.state, id),
    [location.state, id]
  );
  const bookingDraft = React.useMemo(
    () => readBookingDraftRefreshSafe(location.state, id),
    [location.state, id],
  );
  const initialCareSubject = React.useMemo(
    () => normalizeCareSubjectFromBooking(bookingDraft || {}, auth),
    [
      auth?.profile?.display_name,
      auth?.profile?.email,
      auth?.profile?.full_name,
      auth?.profile?.date_of_birth,
      auth?.user?.email,
      auth?.user?.id,
      auth?.user?.user_metadata?.full_name,
      bookingDraft,
    ],
  );
  const [bookingKey] = React.useState(() => {
    const fromState = String(location.state?.bookingKey || "").trim();
    if (fromState) return fromState;

    try {
      const raw = sessionStorage.getItem(`afrimed:booking:${id}`);
      const parsed = raw ? safeJSONParse(raw) : null;
      const storedKey = String(parsed?.bookingKey || "").trim();
      if (storedKey) return storedKey;
    } catch {}

    try {
      const raw = localStorage.getItem(`afrimed:booking:${id}`);
      const parsed = raw ? safeJSONParse(raw) : null;
      const storedKey = String(parsed?.bookingKey || "").trim();
      if (storedKey) return storedKey;
    } catch {}

    return createBookingKey();
  });

  const logBookingEvent = React.useCallback(
    (eventType, metadata = {}) => {
      const userId = String(auth?.user?.id || "").trim();
      if (!userId) return;
      void writeUserActivityEvent({
        userId,
        role: "patient",
        route: `${location.pathname || "/"}${location.search || ""}${location.hash || ""}`,
        eventType,
        metadata: {
          doctor_id: doctorId || id || null,
          booking_key: bookingKey || null,
          ...metadata,
        },
      }).catch(() => {});
    },
    [auth?.user?.id, bookingKey, doctorId, id, location.hash, location.pathname, location.search],
  );

  React.useEffect(() => {
    logBookingEvent("booking_screen_loaded", {
      has_doctor: Boolean(doctor),
      source: "appointments_book",
    });
  }, [doctor, logBookingEvent]);

  const initialSelectedCareSubjectId = React.useMemo(() => {
    const fromState = String(
      location.state?.careSubject?.selectionId ||
        location.state?.careSubject?.dependantId ||
        location.state?.careSubjectId ||
        "",
    ).trim();
    const persistedSelection = readPersistedCareSubjectSelectionRefreshSafe(id, bookingKey);

    // Safety first: if persisted selection is a dependant and route-state says "self",
    // keep the persisted dependant so the flow does not silently flip care subject.
    if (
      fromState === "self" &&
      persistedSelection &&
      persistedSelection !== "self"
    ) {
      return persistedSelection;
    }
    if (fromState) return fromState;
    if (persistedSelection) return persistedSelection;

    const fromDraft = String(
      bookingDraft?.careSubject?.selectionId ||
        bookingDraft?.careSubject?.dependantId ||
        bookingDraft?.careSubjectId ||
        "",
    ).trim();
    if (fromDraft) return fromDraft;

    return initialCareSubject?.selectionId || "self";
  }, [bookingDraft, bookingKey, id, initialCareSubject?.selectionId, location.state]);

  const [paymentMethod, setPaymentMethod] = React.useState(
    () => String(location.state?.paymentMethod || bookingDraft?.paymentMethod || "card").trim() || "card",
  );
  // Insurer acceptance is currently informational only. Until AfriMed has an
  // insurer-authorisation rail, every patient booking remains self-pay.
  const [insurancePaymentMode, setInsurancePaymentMode] = React.useState("self_pay");
  const [selectedServiceId, setSelectedServiceId] = React.useState("");
  const [selectedCareSubjectId, setSelectedCareSubjectId] = React.useState(
    initialSelectedCareSubjectId,
  );
  const skipInitialDraftPersistRef = React.useRef(true);
  const [modeState, setModeState] = React.useState({
    loading: false,
    authorityError: "",
    mode: normalizeMode(location.state?.mode || bookingMeta?.mode || ""),
    consultationType:
      normalizeMode(location.state?.mode || bookingMeta?.mode || "") === "evisit"
        ? "private"
        : "moh",
    evisitEnabled: false,
    hasInPersonOption: false,
    facilities: [],
    facilityId: bookingMeta?.facilityId || null,
    facilityName: bookingMeta?.facilityName || "",
    facility: bookingMeta?.facility || null,
    defaultFacility: bookingMeta?.facility || null,
  });
  const [bookingAuthorityRevision, setBookingAuthorityRevision] = React.useState(0);
  const [authorityDoctorUserId, setAuthorityDoctorUserId] = React.useState("");

  const [slotState, setSlotState] = React.useState({
    loading: false,
    error: "",
    slots: [],
    maxDateISO: null,
    workingDay: null,
  });
  const [bookableDateState, setBookableDateState] = React.useState({
    loading: false,
    error: "",
    dates: [],
    maxDateISO: null,
  });
  const [selectedMonthKey, setSelectedMonthKey] = React.useState("");
  const [paymentState, setPaymentState] = React.useState({
    loading: false,
    error: "",
    methods: [],
    hasUnsupportedOnly: false,
  });
  const [paymentPreferenceRevision, setPaymentPreferenceRevision] = React.useState(0);
  const [supportedProviderMethods, setSupportedProviderMethods] = React.useState([]);
  React.useEffect(() => {
    let alive = true;
    // Provider capability is intentionally loaded without a Patient device
    // filter. The order is provider → clinician preference → Patient runtime.
    void loadSupportedAppointmentPaymentMethods({ forClinicianSetup: true, serviceId: selectedServiceId }).then((methods) => {
      if (alive) setSupportedProviderMethods(methods);
    }).catch(() => { if (alive) setSupportedProviderMethods([]); });
    return () => { alive = false; };
  }, [selectedServiceId]);

  React.useEffect(() => {
    const clinicianId = String(doctor?.id || "").trim();
    if (!clinicianId) return undefined;
    const refresh = () => setPaymentPreferenceRevision((value) => value + 1);
    const onPreferenceSaved = (event) => {
      if (String(event?.detail?.doctorUserId || "").trim() === clinicianId) refresh();
    };
    window.addEventListener("afrimed:payment-preferences-saved", onPreferenceSaved);
    const channel = supabase
      .channel(`appointment-payment-preferences:${clinicianId}`)
      .on("broadcast", { event: "payment-preferences-saved" }, (message) => {
        if (String(message?.payload?.doctorUserId || "").trim() === clinicianId) refresh();
      })
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "doctor_profiles", filter: `user_id=eq.${clinicianId}` },
        refresh,
      )
      .subscribe();
    const onVisible = () => { if (document.visibilityState === "visible") refresh(); };
    window.addEventListener("pageshow", refresh);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("afrimed:payment-preferences-saved", onPreferenceSaved);
      window.removeEventListener("pageshow", refresh);
      document.removeEventListener("visibilitychange", onVisible);
      void supabase.removeChannel(channel);
    };
  }, [doctor?.id]);
  const [insuranceState, setInsuranceState] = React.useState({
    loading: false,
    error: "",
    policy: "",
  });
  const availabilityClock = useAvailabilityClock();
  const [serviceState, setServiceState] = React.useState({
    loading: false,
    error: "",
    services: [],
  });
  const [dependantState, setDependantState] = React.useState({
    loading: !!String(auth?.user?.id || "").trim(),
    error: "",
    options: buildBookingCareSubjectOptions([], auth),
  });

  React.useEffect(() => {
    let alive = true;

    async function hydrateDependants() {
      const guardianUserId = String(auth?.user?.id || "").trim();
      if (!guardianUserId) {
        if (alive) {
          setDependantState({
            loading: false,
            error: "",
            options: buildBookingCareSubjectOptions([], auth),
          });
        }
        return;
      }

      setDependantState((prev) => ({
        ...prev,
        loading: true,
        error: "",
      }));

      try {
        const dependants = await listGuardianDependants(auth);
        if (!alive) return;
        setDependantState({
          loading: false,
          error: "",
          options: buildBookingCareSubjectOptions(dependants, auth),
        });
      } catch (error) {
        if (!alive) return;
        setDependantState({
          loading: false,
          error:
            error?.message ||
            t(
              "appointments.book.errors.dependants",
              "Dependants could not be loaded right now.",
            ),
          options: buildBookingCareSubjectOptions([], auth),
        });
      }
    }

    void hydrateDependants();
    return () => {
      alive = false;
    };
  }, [
    auth,
    auth?.user?.id,
    t,
  ]);

  const careSubjectOptions = React.useMemo(
    () =>
      Array.isArray(dependantState.options) && dependantState.options.length > 0
        ? dependantState.options
        : buildBookingCareSubjectOptions([], auth),
    [auth, dependantState.options],
  );
  const selectedCareSubject = React.useMemo(() => {
    const resolvedSelectionId = String(selectedCareSubjectId || "").trim();
    const selectedOption = careSubjectOptions.find(
      (option) => option.selectionId === resolvedSelectionId,
    );
    if (selectedOption) return selectedOption;

    const initialSelectionId = String(initialCareSubject?.selectionId || "").trim();
    if (
      dependantState.loading &&
      initialCareSubject?.type === "dependant" &&
      initialSelectionId &&
      initialSelectionId === resolvedSelectionId
    ) {
      return initialCareSubject;
    }

    return careSubjectOptions[0] || buildSelfCareSubject(auth);
  }, [
    auth,
    careSubjectOptions,
    dependantState.loading,
    initialCareSubject,
    selectedCareSubjectId,
  ]);
  React.useEffect(() => {
    if (!selectedDate) setSelectedDate(todayISO());

    try {
      const draft = readBookingDraftRefreshSafe(location.state, id);
      if (!draft) return;
      if (!selectedDate && draft.selectedDate) setSelectedDate(draft.selectedDate);
      if (!selectedTime && draft.selectedTime) setSelectedTime(draft.selectedTime);
      if (draft.paymentMethod) setPaymentMethod(draft.paymentMethod);
      if (draft.selectedServiceId) setSelectedServiceId(draft.selectedServiceId);
      const persistedSelection = readPersistedCareSubjectSelectionRefreshSafe(id, bookingKey);
      if (persistedSelection) {
        setSelectedCareSubjectId(persistedSelection);
      } else {
        const nextCareSubject = normalizeCareSubjectFromBooking(draft, auth);
        if (nextCareSubject?.selectionId) {
          setSelectedCareSubjectId(nextCareSubject.selectionId);
        }
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasResolvedAuthenticatedDependants = React.useMemo(() => {
    const userId = String(auth?.user?.id || "").trim();
    return Boolean(userId) && !dependantState.loading;
  }, [auth?.user?.id, dependantState.loading]);

  React.useEffect(() => {
    if (dependantState.loading) return;
    if (auth?.loading) return;

    const persistedSelection = readPersistedCareSubjectSelectionRefreshSafe(id, bookingKey);
    const currentSelection = String(selectedCareSubjectId || "").trim();
    const hasDependantOptions = careSubjectOptions.some(
      (option) => String(option?.type || "").trim().toLowerCase() === "dependant",
    );

    // If we still have a dependant selection in session/local storage, never auto-fall back to self.
    if (
      persistedSelection &&
      persistedSelection !== "self" &&
      currentSelection &&
      currentSelection !== "self"
    ) {
      return;
    }

    // During auth/bootstrap windows, keep non-self selection until we know dependant hydration is complete.
    if (
      currentSelection &&
      currentSelection !== "self" &&
      !hasResolvedAuthenticatedDependants
    ) {
      return;
    }

    if (
      currentSelection &&
      currentSelection !== "self" &&
      hasDependantOptions &&
      !careSubjectOptions.some((option) => option.selectionId === currentSelection)
    ) {
      return;
    }

    const selectedExists = careSubjectOptions.some(
      (option) => option.selectionId === selectedCareSubjectId,
    );
    if (!selectedExists) {
      setSelectedCareSubjectId(careSubjectOptions[0]?.selectionId || "self");
    }
  }, [
    auth?.loading,
    bookingKey,
    careSubjectOptions,
    dependantState.loading,
    hasResolvedAuthenticatedDependants,
    id,
    selectedCareSubjectId,
  ]);

  function handleSelectCareSubject(option) {
    const nextSelectionId = String(option?.selectionId || "self").trim() || "self";
    setSelectedCareSubjectId(nextSelectionId);
    persistCareSubjectSelectionRefreshSafe(id, bookingKey, nextSelectionId);

    const snapshot = {
      bookingKey,
      doctorId: id || doctor?.id || null,
      doctor: bookingDoctor || doctor || null,
      careSubjectId: option?.selectionId || "self",
      careSubjectType: option?.type || "self",
      careSubjectName: option?.patientName || "",
      careSubjectRelationship: option?.relationship || "",
      careSubjectDateOfBirth: option?.dateOfBirth || null,
      careSubjectVerificationStatus: option?.verificationStatus || "",
      careSubjectAvatarPath: option?.avatarPath || "",
      careSubjectAvatarUrl: option?.avatarUrl || "",
      careSubjectAvatarSrc: option?.avatarSrc || "",
      careSubject: option || buildSelfCareSubject(auth),
      selectedDate: selectedDate || null,
      selectedTime: selectedTime || null,
      paymentMethod: paymentMethod || null,
      ...insuranceContext,
      selectedServiceId: selectedServiceId || null,
      serviceId: selectedService?.id || null,
      serviceName: localizeBookingServiceName(selectedService?.name || "", languageCode),
      serviceNamePrice: selectedService ? formatAmount(selectedService.price, selectedService.currency) : "",
      amount: selectedService?.price ?? null,
      duration: selectedService?.durationMinutes ?? null,
      durationMinutes: selectedService?.durationMinutes ?? null,
      duration_minutes: selectedService?.durationMinutes ?? null,
      currency: selectedService?.currency || doctor?.currency || "BWP",
      mode: modeState.mode || null,
      consultationType: modeState.consultationType || null,
      consultation_type: modeState.consultationType || null,
      facilityId: modeState.facilityId || null,
      facilityName: modeState.facilityName || "",
      facility: modeState.facility || null,
    };
    persistBookingDraftRefreshSafe(id, bookingKey, snapshot);
  }

  React.useEffect(() => {
    let alive = true;

    async function hydrateBookingMode() {
      if (!doctor?.id) {
        if (alive) {
          setModeState((prev) => ({
            ...prev,
            loading: false,
          }));
        }
        return;
      }

      const explicitRequestedMode = normalizeMode(location.state?.mode || bookingMeta?.mode || "");
      setModeState((prev) => ({
        ...prev,
        loading: true,
        authorityError: "",
      }));

      try {
        const resolved = await resolveDoctorBookingEntryState({
          doctorId: doctor.id,
          doctor,
          requestedMode: explicitRequestedMode || null,
          consultationType:
            location.state?.consultationType ||
            location.state?.consultation_type ||
            (explicitRequestedMode === "evisit" ? "private" : null),
          facilityId: bookingMeta?.facilityId || null,
          facilityName: bookingMeta?.facilityName || "",
          facility: bookingMeta?.facility || null,
        });

        if (!alive) return;

        // The resolver is authoritative: an old route/draft cannot revive a
        // consultation mode excluded by practice mode or virtual readiness.
        const nextMode = resolved.mode || "";
        const nextFacility =
          nextMode === "inperson"
            ? resolved.facility || resolved.defaultFacility || bookingMeta?.facility || null
            : null;

        setModeState({
          loading: false,
          authorityError: "",
          mode: nextMode,
          consultationType: nextMode === "evisit" ? "private" : "moh",
          evisitEnabled: Boolean(resolved.virtualAvailable),
          hasInPersonOption: Boolean(resolved.inPersonAvailable),
          facilities: Array.isArray(resolved.facilities) ? resolved.facilities : [],
          facilityId: nextMode === "inperson" ? nextFacility?.id || null : null,
          facilityName: nextMode === "inperson" ? nextFacility?.name || "" : "",
          facility: nextMode === "inperson" ? nextFacility : null,
          defaultFacility: resolved.defaultFacility || nextFacility || null,
        });
        setAuthorityDoctorUserId(String(resolved.doctorId || "").trim());
      } catch (error) {
        if (!alive) return;
        setAuthorityDoctorUserId("");
        setModeState({
          loading: false,
          authorityError: String(error?.code || "booking_authority_unavailable"),
          mode: "",
          consultationType: "",
          evisitEnabled: false,
          hasInPersonOption: false,
          facilities: [],
          facilityId: null,
          facilityName: "",
          facility: null,
          defaultFacility: null,
        });
      }
    }

    void hydrateBookingMode();

    return () => {
      alive = false;
    };
  }, [
    bookingMeta?.facility,
    bookingMeta?.facilityId,
    bookingMeta?.facilityName,
    bookingMeta?.mode,
    doctor,
    location.state?.consultationType,
    location.state?.consultation_type,
    location.state?.mode,
    bookingAuthorityRevision,
  ]);

  React.useEffect(() => {
    // Subscribe from the route's canonical clinician reference too, so a
    // recoverable authority error can heal on the next clinician save rather
    // than requiring a refresh before the first successful projection.
    const doctorUserId = String(
      authorityDoctorUserId || doctor?.user_id || doctor?.doctor_user_id || doctor?.id || ""
    ).trim();
    if (!doctorUserId) return undefined;

    const channel = supabase
      .channel(`patient-booking-authority:${doctorUserId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "doctor_booking_authority_invalidations",
          filter: `doctor_user_id=eq.${doctorUserId}`,
        },
        () => setBookingAuthorityRevision((revision) => revision + 1),
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [authorityDoctorUserId, doctor?.doctor_user_id, doctor?.id, doctor?.user_id]);

  React.useEffect(() => {
    let alive = true;

    async function hydrateServices() {
      if (!doctor?.id) {
        if (alive) setServiceState({ loading: false, error: "", services: [] });
        return;
      }

      setServiceState((prev) => ({ ...prev, loading: true, error: "" }));
      try {
        const result = await loadDoctorServices(
          doctor.id,
          doctor,
          modeState.mode || null,
          t
        );
        if (!alive) return;
        setServiceState({
          loading: false,
          error: "",
          services: result.services || [],
        });
      } catch (error) {
        if (!alive) return;
        setServiceState({
          loading: false,
          error:
            error?.message ||
            t(
              "appointments.book.errors.loadServices",
              "Unable to load doctor services."
            ),
          services: normalizeFallbackServices(doctor, modeState.mode || null, t),
        });
      }
    }

    hydrateServices();
    return () => {
      alive = false;
    };
  }, [doctor, modeState.mode, t]);

  const selectedService = React.useMemo(
    () => (serviceState.services || []).find((service) => service.id === selectedServiceId) || null,
    [selectedServiceId, serviceState.services]
  );

  const bookableMonths = React.useMemo(() => {
    const months = [];
    const seen = new Set();
    for (const dateISO of bookableDateState.dates || []) {
      const key = getBookableMonthKey(dateISO);
      if (!key || seen.has(key)) continue;
      seen.add(key);
      months.push({
        key,
        label: formatBookableMonthLabel(dateISO, formatDate),
        firstDate: dateISO,
      });
    }
    return months;
  }, [bookableDateState.dates, formatDate]);

  React.useEffect(() => {
    if (bookableMonths.length === 0) {
      setSelectedMonthKey("");
      return;
    }
    setSelectedMonthKey((current) =>
      current && bookableMonths.some((month) => month.key === current)
        ? current
        : bookableMonths[0].key,
    );
  }, [bookableMonths]);

  const visibleBookableDates = React.useMemo(() => {
    if (!selectedMonthKey) return bookableDateState.dates || [];
    return (bookableDateState.dates || []).filter(
      (dateISO) => getBookableMonthKey(dateISO) === selectedMonthKey,
    );
  }, [bookableDateState.dates, selectedMonthKey]);

  const handleMonthSelect = React.useCallback(
    (monthKey) => {
      const nextMonth = bookableMonths.find((month) => month.key === monthKey);
      if (!nextMonth) return;
      setSelectedMonthKey(monthKey);
      if (!selectedDate || getBookableMonthKey(selectedDate) !== monthKey) {
        setSelectedDate(nextMonth.firstDate);
      }
    },
    [bookableMonths, selectedDate, setSelectedDate],
  );

  React.useEffect(() => {
    let alive = true;

    async function hydrateBookableDates() {
      if (!doctor?.id) {
        if (alive) {
          setBookableDateState({
            loading: false,
            error: "",
            dates: [],
            maxDateISO: null,
          });
        }
        return;
      }

      setBookableDateState((prev) => ({ ...prev, loading: true, error: "" }));
      try {
        const result = await getDoctorBookableDates({
          doctorId: doctor.id,
        });

        if (!alive) return;
        const candidateDates = Array.isArray(result?.dates) ? result.dates : [];

        setBookableDateState((prev) => ({
          ...prev,
          loading: candidateDates.length === 0,
          error: "",
          dates: candidateDates,
          maxDateISO: result?.maxDateISO || prev.maxDateISO || null,
        }));

        setSelectedDate((current) => {
          if (candidateDates.length === 0) return "";
          return current && candidateDates.includes(current) ? current : candidateDates[0];
        });

        const nextDates = await filterDatesWithVisibleSlots({
          doctorId: doctor.id,
          dates: candidateDates,
          requestedDuration: selectedService?.durationMinutes || null,
        });
        if (!alive) return;
        setBookableDateState({
          loading: false,
          error: "",
          dates: nextDates,
          maxDateISO: result?.maxDateISO || null,
        });

        setSelectedDate((current) => {
          if (nextDates.length === 0) return "";
          return current && nextDates.includes(current) ? current : nextDates[0];
        });
      } catch (error) {
        if (!alive) return;
        setBookableDateState((prev) => ({
          ...prev,
          loading: false,
          error:
            error?.message ||
            t(
              "appointments.book.errors.loadDates",
              "Unable to load bookable dates."
            ),
        }));
      }
    }

    hydrateBookableDates();
    return () => {
      alive = false;
    };
  }, [doctor?.id, selectedService?.durationMinutes, setSelectedDate]);

  React.useEffect(() => {
    let alive = true;

    async function hydrateAvailability() {
      if (!doctor?.id || !selectedDate) {
        if (alive) {
          setSlotState({
            loading: false,
            error: "",
            slots: [],
            maxDateISO: null,
            workingDay: null,
          });
        }
        return;
      }

      setSlotState((prev) => ({ ...prev, loading: true, error: "" }));
      try {
        const result = await getDoctorBookingAvailability({
          doctorId: doctor.id,
          dateISO: selectedDate,
          requestedDuration: selectedService?.durationMinutes || null,
        });
        if (!alive) return;
        setSlotState({
          loading: false,
          error: "",
          slots: result.slots || [],
          maxDateISO: result.maxDateISO || null,
          workingDay: result.workingDay || null,
        });
      } catch (error) {
        if (!alive) return;
        setSlotState((prev) => ({
          ...prev,
          loading: false,
          error:
            error?.message ||
            t(
              "appointments.book.errors.loadTimes",
              "Unable to load available times."
            ),
        }));
      }
    }

    hydrateAvailability();
    return () => {
      alive = false;
    };
  }, [availabilityClock, doctor?.id, selectedDate, selectedService?.durationMinutes]);

  React.useEffect(() => {
    let alive = true;

    async function hydrateInsurance() {
      if (!doctor?.id) {
        if (alive) setInsuranceState({ loading: false, error: "", policy: "" });
        return;
      }

      setInsuranceState((prev) => ({ ...prev, loading: true, error: "" }));
      try {
        const { data, error } = await supabase
          .from("doctor_consultancies")
          .select("id,facility_name,insurance_policy,is_primary,sort_order,updated_at")
          .eq("doctor_id", doctor.id)
          .order("is_primary", { ascending: false })
          .order("sort_order", { ascending: true })
          .order("updated_at", { ascending: false })
          .limit(20);

        if (error) throw error;
        if (!alive) return;
        setInsuranceState({
          loading: false,
          error: "",
          policy: pickBookingInsurancePolicy(data, modeState.facilityId, modeState.facilityName),
        });
      } catch {
        if (!alive) return;
        setInsuranceState({ loading: false, error: "", policy: "" });
      }
    }

    hydrateInsurance();
    return () => {
      alive = false;
    };
  }, [doctor?.id, modeState.facilityId, modeState.facilityName]);

  React.useEffect(() => {
    let alive = true;

    async function hydratePayments() {
      if (!doctor?.id) {
        if (alive) {
          setPaymentState({
            loading: false,
            error: "",
            methods: [],
            hasUnsupportedOnly: false,
          });
        }
        return;
      }

      setPaymentState((prev) => ({ ...prev, loading: true, error: "" }));
      try {
        const result = await loadDoctorPaymentMethods(doctor.id, supportedProviderMethods);
        const clinicianMethods = Array.isArray(result.methods) ? result.methods : [];
        // The booking list is the patient-facing payment ledger: retain every
        // known rail so a missing provider capability is visible as disabled,
        // not mistaken for a removed preference. Selection still requires the
        // strictly narrower provider → clinician → device result below.
        const displayCandidates = DOCTOR_ACTIVE_PAYMENT_METHOD_OPTIONS;
        const patientMethods = filterAppointmentPaystackMethodsForPatient(clinicianMethods);
        const patientEligibleMethodIds = new Set(patientMethods.map((method) => method.id));
        const displayedMethods = displayCandidates.map((method) => ({
          ...method,
          patientEligible: patientEligibleMethodIds.has(method.id),
        }));
        recordAppointmentPaymentCapabilityDiagnostics({
          providerMethods: supportedProviderMethods,
          clinicianMeta: result.meta,
          clinicianEnabledMethods: clinicianMethods,
          patientMethods,
          capabilitySource: "appointment_booking",
        });
        if (!alive) return;
        setPaymentState({
          loading: false,
          error: "",
          methods: displayedMethods,
          hasUnsupportedOnly: false,
        });
      } catch (error) {
        if (!alive) return;
        setPaymentState({
          loading: false,
          error:
            error?.message ||
            t(
              "appointments.book.errors.loadPayments",
              "Unable to load payment methods."
            ),
          methods: [],
          hasUnsupportedOnly: false,
        });
      }
    }

    hydratePayments();
    return () => {
      alive = false;
    };
  }, [doctor?.id, supportedProviderMethods, paymentPreferenceRevision]);

  React.useEffect(() => {
    const validIds = new Set((serviceState.services || []).map((service) => service.id));
    if (validIds.size === 0) return;
    if (!selectedServiceId || !validIds.has(selectedServiceId)) {
      setSelectedServiceId(serviceState.services[0]?.id || "");
    }
  }, [selectedServiceId, serviceState.services]);

  React.useEffect(() => {
    const eligibleMethods = (paymentState.methods || []).filter(
      (method) => method.patientEligible !== false,
    );
    const validIds = new Set(eligibleMethods.map((method) => method.id));
    if (validIds.size === 0) return;
    if (!paymentMethod || !validIds.has(paymentMethod)) {
      setPaymentMethod(eligibleMethods[0]?.id || "");
    }
  }, [paymentMethod, paymentState.methods]);

  const insuranceDisplay = React.useMemo(
    () =>
      resolveInsuranceDisplay(
        insuranceState.policy,
        modeState.facility?.insurance_policy,
        modeState.facility?.insurancePolicy,
        doctor?.insurancePolicy,
        doctor?.insurance_policy,
        doctor?.facility?.insurancePolicy,
        doctor?.facility?.insurance_policy
      ),
    [doctor, insuranceState.policy, modeState.facility]
  );
  const insuranceContext = React.useMemo(
    () =>
      buildInsuranceContext({
        mode: insurancePaymentMode,
        provider: "",
        acceptedLabel: insuranceDisplay.raw || insuranceState.policy,
        companies: insuranceDisplay.companies,
        t,
      }),
    [
      insuranceDisplay.companies,
      insuranceDisplay.raw,
      insurancePaymentMode,
      insuranceState.policy,
      t,
    ],
  );

  const bookingDoctor = React.useMemo(() => {
    if (!doctor) return null;
    const insurancePatch = {
      insurancePolicy:
        insuranceDisplay.raw ||
        doctor?.insurancePolicy ||
        doctor?.insurance_policy ||
        doctor?.facility?.insurancePolicy ||
        doctor?.facility?.insurance_policy ||
        "",
      insurance_policy:
        insuranceDisplay.raw ||
        doctor?.insurance_policy ||
        doctor?.insurancePolicy ||
        doctor?.facility?.insurance_policy ||
        doctor?.facility?.insurancePolicy ||
        "",
    };
    if (!selectedService) {
      return {
        ...doctor,
        ...insurancePatch,
      };
    }
    return {
      ...doctor,
      ...insurancePatch,
      id: doctor?.id || id || null,
      serviceLabel: localizeBookingServiceName(selectedService.name, languageCode),
      serviceLabelPrice: selectedService.price.toFixed(2),
      serviceDurationMinutes: normalizeDurationMinutes(selectedService.durationMinutes),
      currency: selectedService.currency || doctor.currency || "BWP",
    };
  }, [doctor, id, insuranceDisplay.raw, languageCode, selectedService]);

  React.useEffect(() => {
    const cachedDoctor = bookingDoctor || doctor || null;
    const doctorId = String(cachedDoctor?.id || id || "").trim();
    if (!cachedDoctor || !doctorId) return;

    try {
      sessionStorage.setItem(`afrimed:doctor:${doctorId}`, JSON.stringify({ ...cachedDoctor, id: doctorId }));
      sessionStorage.setItem("afrimed:lastDoctor", JSON.stringify({ ...cachedDoctor, id: doctorId }));
      localStorage.setItem(`afrimed:doctor:${doctorId}`, JSON.stringify({ ...cachedDoctor, id: doctorId }));
      localStorage.setItem("afrimed:lastDoctor", JSON.stringify({ ...cachedDoctor, id: doctorId }));
    } catch {
      // ignore cache failures
    }
  }, [bookingDoctor, doctor, id]);

  const bookingExitState = React.useMemo(
    () => ({
      ...(location.state || {}),
      bookingKey,
      doctorId: id || doctor?.id || null,
      doctor: bookingDoctor || doctor || null,
    }),
    [bookingDoctor, bookingKey, doctor, id, location.state],
  );

  function handleCloseBooking() {
    const doctorId = String(id || doctor?.id || "").trim();
    if (!doctorId) {
      nav("/appointments/doctors");
      return;
    }

    nav(`/appointments/doctor/${doctorId}`, {
      state: bookingExitState,
    });
  }

  React.useEffect(() => {
    if (skipInitialDraftPersistRef.current) {
      skipInitialDraftPersistRef.current = false;
      return;
    }
    const resolvedSelectionId = String(selectedCareSubjectId || "").trim();
    const selectedOptionResolved = careSubjectOptions.some(
      (option) => option.selectionId === resolvedSelectionId,
    );
    if (
      dependantState.loading &&
      resolvedSelectionId &&
      resolvedSelectionId !== "self" &&
      !selectedOptionResolved
    ) {
      return;
    }
    try {
      const snapshot = {
        bookingKey,
        doctorId: id || doctor?.id || null,
        doctor: bookingDoctor || doctor || null,
        careSubjectId: selectedCareSubject?.selectionId || "self",
        careSubjectType: selectedCareSubject?.type || "self",
        careSubjectName: selectedCareSubject?.patientName || "",
      careSubjectRelationship: selectedCareSubject?.relationship || "",
      careSubjectDateOfBirth: selectedCareSubject?.dateOfBirth || null,
      careSubjectVerificationStatus: selectedCareSubject?.verificationStatus || "",
      careSubjectAvatarPath: selectedCareSubject?.avatarPath || "",
      careSubjectAvatarUrl: selectedCareSubject?.avatarUrl || "",
      careSubjectAvatarSrc: selectedCareSubject?.avatarSrc || "",
      careSubject: selectedCareSubject || buildSelfCareSubject(auth),
        selectedDate: selectedDate || null,
        selectedTime: selectedTime || null,
        paymentMethod: paymentMethod || null,
        ...insuranceContext,
        selectedServiceId: selectedServiceId || null,
        serviceId: selectedService?.id || null,
        serviceName: localizeBookingServiceName(selectedService?.name || "", languageCode),
        serviceNamePrice: selectedService ? formatAmount(selectedService.price, selectedService.currency) : "",
        amount: selectedService?.price ?? null,
        duration: selectedService?.durationMinutes ?? null,
        durationMinutes: selectedService?.durationMinutes ?? null,
        duration_minutes: selectedService?.durationMinutes ?? null,
        currency: selectedService?.currency || doctor?.currency || "BWP",
        mode: modeState.mode || null,
        consultationType: modeState.consultationType || null,
        consultation_type: modeState.consultationType || null,
        facilityId: modeState.facilityId || null,
        facilityName: modeState.facilityName || "",
        facility: modeState.facility || null,
      };
      sessionStorage.setItem(`afrimed:booking:${id}`, JSON.stringify(snapshot));
      sessionStorage.setItem("afrimed:booking:last", JSON.stringify(snapshot));
      sessionStorage.setItem(`afrimed:bookingKey:${bookingKey}`, JSON.stringify(snapshot));
      localStorage.setItem(`afrimed:booking:${id}`, JSON.stringify(snapshot));
      localStorage.setItem("afrimed:booking:last", JSON.stringify(snapshot));
      localStorage.setItem(`afrimed:bookingKey:${bookingKey}`, JSON.stringify(snapshot));
    } catch {}
    persistCareSubjectSelectionRefreshSafe(id, bookingKey, selectedCareSubject?.selectionId || "self");
  }, [
    bookingKey,
    id,
    doctor,
    bookingDoctor,
    auth,
    selectedDate,
    selectedTime,
    paymentMethod,
    insuranceContext,
    selectedCareSubjectId,
    careSubjectOptions,
    dependantState.loading,
    selectedCareSubject,
    selectedServiceId,
    selectedService,
    modeState.consultationType,
    modeState.facility,
    modeState.facilityId,
    modeState.facilityName,
    modeState.mode,
    languageCode,
  ]);

  const visibleSlotEntries = React.useMemo(
    () => (slotState.slots || []).filter((slot) => !slot?.hidden),
    [slotState.slots]
  );

  const slots = React.useMemo(
    () => visibleSlotEntries.map((slot) => slot.time),
    [visibleSlotEntries]
  );

  const unavailable = React.useMemo(
    () =>
      new Set(
        visibleSlotEntries
          .filter((slot) => !slot.available)
          .map((slot) => slot.time)
      ),
    [visibleSlotEntries]
  );

  React.useEffect(() => {
    if (!selectedTime) return;
    if (!slots.includes(selectedTime) || unavailable.has(selectedTime)) {
      setSelectedTime("");
    }
  }, [selectedTime, slots, unavailable, setSelectedTime]);

  React.useEffect(() => {
    if (slotState.loading || slotState.error || selectedDate !== todayISO()) return;
    if (slots.length > 0) return;
    const nextDate = (bookableDateState.dates || []).find((dateISO) => dateISO > selectedDate);
    if (nextDate) {
      setSelectedDate(nextDate);
    }
  }, [
    bookableDateState.dates,
    selectedDate,
    setSelectedDate,
    slotState.error,
    slotState.loading,
    slots.length,
  ]);

  const canConfirm =
    !!doctor &&
    !!modeState.mode &&
    !!selectedService &&
    !!selectedDate &&
    !!selectedTime &&
    !!paymentMethod &&
    (modeState.mode !== "inperson" || !!(modeState.facilityId || modeState.facility?.id || modeState.facilityName)) &&
    (paymentState.methods || []).some(
      (method) => method.id === paymentMethod && method.patientEligible !== false,
    ) &&
    slots.includes(selectedTime) &&
    !unavailable.has(selectedTime);

  const handleContinueToCheckout = React.useCallback(() => {
    logBookingEvent("booking_continue_clicked", {
      cta_enabled: canConfirm,
      has_selected_date: Boolean(selectedDate),
      has_selected_time: Boolean(selectedTime),
      payment_method_selected: Boolean(paymentMethod),
      disabled_visible_is_not_missing: true,
    });

    if (!canConfirm) return;

    nav(`/appointments/checkout/${id}`, {
      state: {
        bookingKey,
        doctor: bookingDoctor || doctor,
        // Keep the authority resolver's clinician *user* ID alongside the
        // public/profile route ID. Payment functions authorise services
        // against doctor_services.doctor_id, which is this user ID.
        doctorUserId: authorityDoctorUserId || doctor?.user_id || doctor?.doctor_user_id || "",
        careSubjectId: selectedCareSubject?.selectionId || "self",
        careSubjectType: selectedCareSubject?.type || "self",
        careSubjectName: selectedCareSubject?.patientName || "",
        careSubjectRelationship: selectedCareSubject?.relationship || "",
        careSubjectDateOfBirth: selectedCareSubject?.dateOfBirth || null,
        careSubjectVerificationStatus: selectedCareSubject?.verificationStatus || "",
        careSubjectAvatarPath: selectedCareSubject?.avatarPath || "",
        careSubjectAvatarUrl: selectedCareSubject?.avatarUrl || "",
        careSubjectAvatarSrc: selectedCareSubject?.avatarSrc || "",
        careSubject: selectedCareSubject || buildSelfCareSubject(auth),
        serviceId: selectedService?.id || null,
        serviceName: localizeBookingServiceName(selectedService?.name || "", languageCode),
        serviceNamePrice: selectedService
          ? formatAmount(selectedService.price, selectedService.currency)
          : "",
        duration: selectedService?.durationMinutes ?? null,
        durationMinutes: selectedService?.durationMinutes ?? null,
        duration_minutes: selectedService?.durationMinutes ?? null,
        selectedServiceId: selectedService?.id || null,
        selectedDate,
        selectedTime,
        paymentMethod,
        ...insuranceContext,
        paymentMethodLabel:
          localizePaymentMethodLabel(
            paymentState.methods.find((method) => method.id === paymentMethod)?.id ||
              paymentMethod,
            t,
            paymentState.methods.find((method) => method.id === paymentMethod)?.label ||
              "",
          ),
        mode: modeState.mode || null,
        consultationType: modeState.consultationType || null,
        consultation_type: modeState.consultationType || null,
        facilityId: modeState.facilityId || null,
        facilityName: modeState.facilityName || "",
        facility: modeState.facility || null,
        amount: selectedService?.price ?? null,
        currency: selectedService?.currency || doctor?.currency || "BWP",
      },
    });
  }, [
    auth,
    authorityDoctorUserId,
    bookingDoctor,
    bookingKey,
    canConfirm,
    doctor,
    id,
    insuranceContext,
    logBookingEvent,
    modeState.consultationType,
    modeState.facility,
    modeState.facilityId,
    modeState.facilityName,
    modeState.mode,
    nav,
    paymentMethod,
    paymentState.methods,
    selectedCareSubject,
    selectedDate,
    selectedService,
    selectedTime,
    t,
  ]);
  const evisitDisabled = !modeState.loading && !modeState.evisitEnabled;
  const inpersonDisabled = !modeState.loading && !modeState.hasInPersonOption;
  const clinicianTemporarilyUnbookable =
    !modeState.loading &&
    !modeState.authorityError &&
    !modeState.evisitEnabled &&
    !modeState.hasInPersonOption;
  const facilityItems = React.useMemo(
    () =>
      (modeState.facilities || []).map((facilityOption, index) => {
        const optionId = String(facilityOption?.id || "").trim();
        const optionName = String(facilityOption?.name || "").trim();
        const isSelected =
          (optionId && optionId === String(modeState.facilityId || "").trim()) ||
          (!optionId &&
            optionName &&
            optionName === String(modeState.facilityName || "").trim());

        return {
          title: optionName || "Facility",
          support: facilityOption?.address || "",
          truncateSupport: false,
          trailing: <Radio checked={isSelected} variant="afrimed" />,
          divider: index !== (modeState.facilities || []).length - 1,
          onClick: () =>
            setModeState((prev) => ({
              ...prev,
              facilityId: optionId || null,
              facilityName: optionName || "",
              facility: facilityOption || null,
            })),
        };
      }),
    [modeState.facilities, modeState.facilityId, modeState.facilityName]
  );

  return (
    <div className="min-h-[100dvh] min-w-0 overflow-x-clip bg-[rgb(var(--clr-surface-container-low))]">
      <div className="afrimed-rail afrimed-rail--comfortable min-w-0 pt-4 pb-24">
        <TopBarCenter
          title={t("appointments.book.title", "Choose a date, type and time")}
          close
          onBack={handleCloseBooking}
        />

        {!doctor ? (
          <Card className="mt-4 px-4 py-4 text-body-medium text-on-surface-var">
            {t(
              "appointments.book.doctorMissing",
              "Doctor not found. Please go back and select a doctor again."
            )}
            <div className="mt-3">
              <Button fullWidth onClick={() => nav("/appointments/doctors")}>
                {t("appointments.book.backToDoctors", "Back to doctors")}
              </Button>
            </div>
          </Card>
        ) : null}

        <div className="mt-8">
          <div className="mb-3 flex items-center justify-between gap-3">
            <div className="text-body-large text-on-surface-var">
              {t("appointments.book.sections.patientSubject", "Who is this appointment for?")}
            </div>
          </div>

          <List
            items={careSubjectOptions.map((option, index) => {
              const dependantDisplayName =
                option.type === "dependant"
                  ? [
                      String(option.firstName || "").trim(),
                      String(option.lastName || "").trim(),
                    ]
                      .filter(Boolean)
                      .join(" ")
                      .trim() ||
                    String(option.patientName || "").trim() ||
                    "Dependant"
                  : "";
              const support =
                option.type === "self"
                  ? t(
                      "appointments.book.subjectSelf",
                      "Your account stays the booking owner and payer.",
                    )
                  : [dependantDisplayName, String(option.relationship || "").trim()]
                      .filter(Boolean)
                      .join(" • ");

              return {
                title:
                  option.type === "self"
                    ? t("appointments.book.myself", "Myself")
                    : t("appointments.book.dependant", "Dependant"),
                support,
                leading:
                  option.type === "dependant" ? (
                    <span className="relative inline-flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[rgb(var(--clr-surface))] shadow-[0px_0px_0px_1px_rgba(24,50,119,0.10),0px_1.5px_0px_-0.5px_rgba(26,27,35,0.06),0px_3px_3px_-1px_rgba(26,27,35,0.06),0px_3px_6px_-2px_rgba(26,27,35,0.06),0px_6px_12px_-4px_rgba(29,79,215,0.06),0px_12px_24px_-10px_rgba(29,79,215,0.06)]">
                      <span className="pointer-events-none absolute inset-0 rounded-full bg-gradient-to-b from-white/80 to-white/0 mix-blend-overlay" />
                      <img
                        src={option.avatarSrc || resolveDependantAvatarSrc(option)}
                        alt=""
                        className="relative h-full w-full rounded-full object-cover"
                        draggable="false"
                      />
                    </span>
                  ) : null,
                divider: index !== careSubjectOptions.length - 1,
                trailing: (
                  <Radio
                    checked={selectedCareSubjectId === option.selectionId}
                    variant="afrimed"
                  />
                ),
                onClick: () => handleSelectCareSubject(option),
              };
            })}
          />

          {dependantState.loading ? (
            <div className="mt-3 text-body-medium text-on-surface-var">
              {t("appointments.book.loading.dependants", "Loading dependants...")}
            </div>
          ) : null}

          {!dependantState.loading && dependantState.error ? (
            <div className="mt-3 text-body-medium text-[rgb(var(--schemes-critical))]">
              {dependantState.error}
            </div>
          ) : null}
        </div>

        <div className="mt-8">
          <div className="mb-3 flex items-center justify-between gap-3">
            <div className="text-body-large text-on-surface-var">
              {t("appointments.book.sections.service", "Select a service")}
            </div>
            <ContextFeedbackLauncher
              section="appointments-book-service"
              context={{ surface: "appointments.book", doctorId }}
              entity={{ type: "doctor", id: doctorId || null }}
            />
          </div>

          <List
            items={(serviceState.services || []).map((service, idx) => {
              const selected = selectedServiceId === service.id;
              return {
                title: localizeBookingServiceName(service.name, languageCode),
                support: t("appointments.book.minutesShort", "{count} min", {
                  count: normalizeDurationMinutes(service.durationMinutes),
                }),
                divider: idx !== (serviceState.services || []).length - 1,
                trailing: (
                  <span className="flex items-center gap-3">
                    <span className="text-body-large text-on-surface whitespace-nowrap">
                      {formatAmount(service.price, service.currency)}
                    </span>
                    <Radio checked={selected} variant="afrimed" />
                  </span>
                ),
                onClick: () => setSelectedServiceId(service.id),
              };
            })}
          />

          {serviceState.loading ? (
            <div className="mt-3 text-body-medium text-on-surface-var">
              {t("appointments.book.loading.services", "Loading doctor services...")}
            </div>
          ) : null}

          {!serviceState.loading && serviceState.error ? (
            <div className="mt-3 text-body-medium text-[rgb(var(--schemes-critical))]">
              {serviceState.error}
            </div>
          ) : null}

          {!serviceState.loading && !serviceState.error && (serviceState.services || []).length === 0 ? (
            <Card className="mt-3 px-4 py-3 text-body-medium text-on-surface-var">
              {t(
                "appointments.book.empty.services",
                "This doctor has not added services yet."
              )}
            </Card>
          ) : null}
        </div>

        <div className="mt-8">
          <div className="mb-3 flex items-center justify-between gap-3">
            <div className="text-body-large text-on-surface-var">
              {t("appointments.book.sections.type", "Select your appointment type")}
            </div>
            <ContextFeedbackLauncher
              section="appointments-book-type"
              context={{ surface: "appointments.book", doctorId, bookingMode: modeState.mode }}
              entity={{ type: "doctor", id: doctorId || null }}
            />
          </div>

          <div className="chips-row">
            <AfriPill
              label={t("appointments.book.mode.virtual", "Virtual")}
              value="evisit"
              iconLeft="/icons/appointments/video_call.svg"
              variant="appointments"
              size="medium"
              isActive={modeState.mode === "evisit"}
              disabled={evisitDisabled}
              onChange={() => {
                if (evisitDisabled) return;
                setModeState((prev) => ({
                  ...prev,
                  mode: "evisit",
                  consultationType: "private",
                  facilityId: null,
                  facilityName: "",
                  facility: null,
                }));
              }}
            />

            <AfriPill
              label={t("appointments.book.mode.inPerson", "In-person")}
              value="inperson"
              iconLeft="/icons/appointments/person.svg"
              variant="appointments"
              size="medium"
              isActive={modeState.mode === "inperson"}
              disabled={inpersonDisabled}
              onChange={() => {
                if (inpersonDisabled) return;
                setModeState((prev) => ({
                  ...prev,
                  mode: "inperson",
                  consultationType: "moh",
                  facilityId: prev.defaultFacility?.id || prev.facilityId || null,
                  facilityName: prev.defaultFacility?.name || prev.facilityName || "",
                  facility: prev.defaultFacility || prev.facility || null,
                }));
              }}
            />
          </div>

          {modeState.loading ? (
            <div className="mt-3 text-body-medium text-on-surface-var">
              {t("appointments.book.loading.type", "Loading appointment types...")}
            </div>
          ) : null}

          {modeState.authorityError ? (
            <div className="mt-3 text-body-medium text-[rgb(var(--schemes-critical))]" role="alert">
              {t(
                "appointments.book.authorityUnavailable",
                "Appointment options are temporarily unavailable. Please try again.",
              )}
            </div>
          ) : null}

          {clinicianTemporarilyUnbookable ? (
            <div className="mt-3 text-body-medium text-on-surface-var" role="status">
              {t(
                "appointments.book.clinicianTemporarilyUnavailable",
                "This clinician is temporarily unavailable for booking.",
              )}
            </div>
          ) : null}

          {!modeState.authorityError && !clinicianTemporarilyUnbookable && evisitDisabled ? (
            <div className="mt-3 text-body-medium text-on-surface-var">
              {t(
                "appointments.book.evisitUnavailable",
                "Virtual appointments are not enabled for this doctor yet, so booking will stay aligned to in-person availability."
              )}
            </div>
          ) : null}

          {modeState.mode === "evisit" ? (
            <div className="mt-3 text-body-medium text-on-surface-var">
              {t(
                "appointments.book.virtualVisitNotice",
                "This booking will be a virtual appointment for the fastest patient-friendly path."
              )}
            </div>
          ) : (
            <div className="mt-3">
              <SectionLabelWithIcon
                iconSrc={HospitalFillIcon}
                iconAlt=""
                label={t("appointments.book.sections.location", "Location")}
              />
              <div className="mt-1 text-body-large text-on-surface">
                {modeState.facilityName ||
                  t(
                    "appointments.book.inpersonFallback",
                    "In-person appointment at the doctor's consult location."
                  )}
              </div>

              {modeState.facilities.length > 1 ? (
                <div className="mt-3">
                  <List items={facilityItems} />
                </div>
              ) : (
                <div className="mt-3">
                  <List
                    items={[
                      {
                        title:
                          modeState.facilityName ||
                          t("appointments.book.inpersonFallback", "In-person appointment at the doctor's consult location."),
                        support: modeState.facility?.address || "",
                        truncateSupport: false,
                        divider: false,
                        trailing: <Radio checked variant="afrimed" />,
                        onClick: () => {},
                      },
                    ]}
                  />
                </div>
              )}
            </div>
          )}
        </div>

        <div className="mt-8">
          <div className="mb-2 flex items-center justify-between gap-3">
            <SectionLabelWithIcon
              iconSrc="/icons/doctor/calendar_clock.svg"
              iconAlt=""
              label={t("appointments.book.sections.date", "Date")}
            />
            <ContextFeedbackLauncher
              section="appointments-book-date"
              context={{ surface: "appointments.book", doctorId, selectedDate }}
              entity={{ type: "doctor", id: doctorId || null }}
            />
          </div>

          <div className="px-0 py-1">
            <div className="text-body-large text-on-surface">
              {selectedDate
                ? formatDate(selectedDate, {
                    weekday: "long",
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  })
                : t("appointments.book.selectDate", "Select date")}
            </div>

            {bookableDateState.loading && bookableDateState.dates.length === 0 ? (
              <div className="mt-3 text-body-medium text-on-surface-var">
                {t("appointments.book.loading.dates", "Loading available dates...")}
              </div>
            ) : null}

            {bookableDateState.error && bookableDateState.dates.length === 0 ? (
              <div className="mt-3 text-body-medium text-[rgb(var(--schemes-critical))]">
                {bookableDateState.error}
              </div>
            ) : null}

            {!bookableDateState.loading &&
            !bookableDateState.error &&
            bookableDateState.dates.length === 0 ? (
              <div className="mt-3 text-body-medium text-on-surface-var">
                {t(
                  "appointments.book.empty.dates",
                    "This doctor has not opened any appointment days yet."
                )}
              </div>
            ) : null}

            {bookableMonths.length > 0 ? (
              <div className="mt-4 w-full min-w-0 overflow-x-auto overscroll-x-contain" aria-label={t("appointments.book.months.label", "Available months")}>
                <div className="chips-row w-max min-w-full">
                  {bookableMonths.map((month) => (
                    <AfriPill
                      key={month.key}
                      label={month.label}
                      value={month.key}
                      variant="appointments"
                      size="medium"
                      isActive={selectedMonthKey === month.key}
                      onChange={handleMonthSelect}
                    />
                  ))}
                </div>
              </div>
            ) : null}

            {visibleBookableDates.length > 0 ? (
              <div className="mt-4 grid min-w-0 grid-cols-[repeat(3,minmax(0,1fr))] gap-3 sm:grid-cols-[repeat(4,minmax(0,1fr))] lg:grid-cols-[repeat(5,minmax(0,1fr))]">
                {visibleBookableDates.map((dateISO) => {
                  const parts = formatBookableDateParts(dateISO, formatDate);
                  const isSelected = selectedDate === dateISO;

                  return (
                    <Button
                      key={dateISO}
                      type="button"
                      variant={isSelected ? "default" : "tonal"}
                      onClick={() => setSelectedDate(dateISO)}
                      className={[
                        "h-auto min-h-[88px] min-w-0 flex-col gap-1 rounded-[12px] px-3 py-3 text-center",
                        isSelected
                          ? "bg-[rgb(var(--afrimed-primary))] text-[rgb(var(--afrimed-on-primary))] shadow-[0_4px_10px_rgba(38,82,227,0.18)]"
                          : "bg-[var(--state-primary-12)] text-primary shadow-none",
                      ].join(" ")}
                    >
                      <span className="text-label-medium uppercase tracking-[0.04em]">
                        {parts.weekday}
                      </span>
                      <span className="text-title-large leading-none">
                        {parts.day}
                      </span>
                      <span className="text-label-medium">{parts.month}</span>
                    </Button>
                  );
                })}
              </div>
            ) : null}
          </div>
        </div>

        <div className="mt-6">
          <div className="mb-3 flex items-center justify-between gap-3">
            <SectionLabelWithIcon
              iconSrc="/icons/appointments/dr-e-prescription/time.svg"
              iconAlt=""
              label={t("appointments.book.sections.time", "Time")}
            />
            <ContextFeedbackLauncher
              section="appointments-book-time"
              context={{ surface: "appointments.book", doctorId, selectedDate }}
              entity={{ type: "doctor", id: doctorId || null }}
            />
          </div>

          {slotState.loading && slots.length === 0 ? (
            <div className="mb-3 text-body-medium text-on-surface-var">
              {t("appointments.book.loading.times", "Loading available times...")}
            </div>
          ) : null}

          {slotState.error && slots.length === 0 ? (
            <div className="mb-3 text-body-medium text-[rgb(var(--schemes-critical))]">
              {slotState.error}
            </div>
          ) : null}

          {!slotState.loading && !slotState.error && slots.length === 0 ? (
            <div className="mb-3 text-body-medium text-on-surface-var">
              {slotState.workingDay?.enabled === false
                ? t(
                    "appointments.book.empty.unavailableDay",
                    "This doctor is not available on the selected day."
                  )
                : t(
                    "appointments.book.empty.noTimes",
                    "No available appointment times remain for this day."
                  )}
            </div>
          ) : null}

          <div className="grid min-w-0 grid-cols-[repeat(4,minmax(0,1fr))] gap-3 md:grid-cols-[repeat(6,minmax(0,1fr))]">
            {slots.map((time) => {
              const isUnavailable = unavailable.has(time);
              const isSelected = selectedTime === time;

              return (
                <Button
                  key={time}
                  type="button"
                  variant={isSelected ? "default" : "tonal"}
                  disabled={isUnavailable}
                  onClick={() => {
                    if (isUnavailable) return;
                    setSelectedTime(time);
                  }}
                  className={[
                    "h-11 min-w-0 rounded-[10px] px-2 text-label-large",
                    isSelected ? "text-[rgb(var(--afrimed-on-primary))]" : "",
                    !isSelected && !isUnavailable ? "bg-[var(--state-primary-12)] text-primary" : "",
                    isUnavailable ? "opacity-50" : "",
                  ].join(" ")}
                >
                  <span className={isUnavailable ? "line-through" : ""}>{time}</span>
                </Button>
              );
            })}
          </div>
        </div>

        <div className="mt-8">
          <div className="mb-3 flex items-center justify-between gap-3">
            <div className="text-body-large text-on-surface-var">
              {t(
                "appointments.book.sections.payment",
                "Select your payment method"
              )}
            </div>
            <ContextFeedbackLauncher
              section="appointments-book-payment"
              context={{ surface: "appointments.book", doctorId, paymentMethod }}
              entity={{ type: "doctor", id: doctorId || null }}
            />
          </div>

          <div className="mb-3">
            <div className="text-body-large text-on-surface-var">
              {t("appointments.book.insuranceAccepted", "Insurances accepted")}
            </div>
            {insuranceDisplay.hasActual ? (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {insuranceDisplay.companies.map((company) => (
                  <span
                    key={company}
                    className="inline-flex min-h-[32px] items-center rounded-lg bg-[var(--state-primary-12)] px-2.5 text-label-large text-primary"
                  >
                    {company}
                  </span>
                ))}
              </div>
            ) : (
              <div className="mt-1 text-body-medium text-on-surface-var">
                {insuranceDisplay.generic
                  ? t(
                      "appointments.book.insuranceGeneric",
                      "Accepted insurers are shown for your reference. Cover authorisation is not available yet."
                    )
                  : t("appointments.book.insuranceNotListed", "Insurance not listed")}
              </div>
            )}

            <div className="mt-4">
              <div className="text-label-large text-on-surface-var">
                {t("appointments.book.insuranceUseLabel", "Use insurance")}
              </div>
              <div className="mt-2 grid grid-cols-2 gap-2">
                <button
                  type="button"
                  className={[
                    "min-h-[56px] rounded-[12px] px-3 text-label-large",
                    insurancePaymentMode === "self_pay"
                      ? "bg-primary text-[rgb(var(--afrimed-on-primary))]"
                      : "bg-[var(--state-primary-12)] text-primary",
                  ].join(" ")}
                  onClick={() => setInsurancePaymentMode("self_pay")}
                >
                  {t("appointments.book.insuranceSelfPay", "Self-pay")}
                </button>
                <button
                  type="button"
                  disabled
                  aria-disabled="true"
                  className="flex min-h-[56px] cursor-not-allowed flex-col items-center justify-center gap-0.5 rounded-xl bg-[rgba(86,106,139,0.08)] px-3 text-label-large leading-tight text-[rgba(86,106,139,0.65)]"
                >
                  <span>{t("appointments.book.insuranceUse", "Use insurance")}</span>
                  <span className="inline-flex items-center rounded-md bg-[rgba(86,106,139,0.12)] px-1.5 py-0.5 text-[9px] font-semibold uppercase leading-3 tracking-[0.02em] text-on-surface-var">
                    {t("appointments.book.comingSoon", "COMING SOON")}
                  </span>
                </button>
              </div>
            </div>
          </div>

              <List
                items={(paymentState.methods || []).map((method, idx) => {
                  const selected = paymentMethod === method.id;
                  const eligible = method.patientEligible !== false;
                  return {
                    // samsung-pay.svg has transparent canvas around its mark;
                    // compensate only in this shared 24px booking icon slot.
                    leading: (
                      <img
                        src={method.icon}
                        alt=""
                        aria-hidden="true"
                        className={`h-6 w-6 object-contain ${method.id === "samsung_pay" ? "scale-[1.5]" : ""}`}
                      />
                    ),
                title: localizePaymentMethodLabel(method.id || method.label, t, method.label),
                support:
                  !eligible && method.id === "apple_pay"
                    ? t("appointments.book.applePayUnavailable", "Available on an Apple device in Safari.")
                    : !eligible && method.id === "google_pay"
                      ? t("appointments.book.googlePayUnavailable", "Available with Google Pay on an eligible device.")
                      : !eligible && method.id === "samsung_pay"
                      ? t("appointments.book.samsungPayUnavailable", "Available with Samsung Pay on an eligible Samsung device.")
                        : !eligible
                          ? t("appointments.book.paymentUnavailable", "This payment method is not available for checkout yet.")
                          : undefined,
                divider: idx !== (paymentState.methods || []).length - 1,
                disabled: !eligible,
                trailing: <Radio checked={selected} disabled={!eligible} variant="afrimed" />,
                onClick: eligible ? () => setPaymentMethod(method.id) : undefined,
              };
            })}
          />

          {paymentState.loading ? (
            <div className="mt-3 text-body-medium text-on-surface-var">
              {t(
                "appointments.book.loading.payments",
                "Loading doctor payment methods..."
              )}
            </div>
          ) : null}

          {!paymentState.loading && paymentState.error ? (
            <div className="mt-3 text-body-medium text-[rgb(var(--schemes-critical))]">
              {paymentState.error}
            </div>
          ) : null}

          {!paymentState.loading && !paymentState.error && (paymentState.methods || []).length === 0 ? (
            <Card className="mt-3 px-4 py-3 text-body-medium text-on-surface-var">
              {paymentState.hasUnsupportedOnly
                ? t(
                    "appointments.book.empty.onlinePaymentsOnly",
                    "This doctor has not enabled secure online card checkout yet. Booking stays disabled until a real payment method is available."
                  )
                : t(
                    "appointments.book.empty.payments",
                    "This doctor has not set payment methods yet."
                  )}
            </Card>
          ) : null}
        </div>

        <div className="mt-8 flex gap-3">
          <Button variant="ghost" className="h-12 flex-1" onClick={() => nav(-1)}>
            {t("common.cancel", "Cancel")}
          </Button>

          <Button
            className="h-12 flex-1"
            disabled={!canConfirm}
            onClick={handleContinueToCheckout}
          >
            {t("appointments.book.confirm", "Confirm")}
          </Button>
        </div>
      </div>
    </div>
  );
}
